import React, { useState, useEffect } from 'react';
import axios from 'axios';
import utils from "../../utils";
import {
  Box, Button, Typography, Grid, List, ListItem, ListItemIcon, ListItemText,
  ListItemButton, Divider, IconButton, TextField, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, Select, MenuItem, Chip, Slider, InputAdornment,
  Card, CardContent, Drawer, Hidden, alpha, Paper,
} from '@mui/material';
import {
  Folder as FolderIcon, AddCircleOutline as AddCircleOutlineIcon, InsertDriveFile as InsertDriveFileIcon,
  Delete as DeleteIcon, Search as SearchIcon, CloudUpload as CloudUploadIcon, Label as LabelIcon,
  Event as EventIcon, Notifications as NotificationsIcon, Close as CloseIcon, CalendarToday as CalendarTodayIcon,
  AccessTime as AccessTimeIcon, Add as AddIcon, Warning as WarningIcon, Menu as MenuIcon,
  Restore as RestoreIcon, DeleteSweep as DeleteSweepIcon, Visibility as VisibilityIcon, GetApp as GetAppIcon,
} from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#f5f5f5',
    },
  },
  components: {
    MuiChip: {
      styleOverrides: {
        root: {
          margin: '2px',
        },
      },
    },
  },
});

const mimeTypes = {
  // Documenti
  pdf: "application/pdf",

  // Immagini
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  svg: "image/svg+xml",
  webp: "image/webp",
  bmp: "image/bmp",
  ico: "image/x-icon",

  // Audio
  mp3: "audio/mpeg",
  wav: "audio/wav",
  ogg: "audio/ogg",
  aac: "audio/aac",

  // Video
  mp4: "video/mp4",
  webm: "video/webm",
  ogv: "video/ogg",

  // Testo
  html: "text/html",
  txt: "text/plain",
  json: "application/json",
  xml: "application/xml",
  css: "text/css",
  js: "application/javascript"
};

export default function FileManager() {
  const drawerWidth = 280;
  const maxFileNameLength = 20;

  const [disabilitato, setDisabilitato] = useState(false);
  const [isOp, setIsOp] = useState(false);

  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [trash, setTrash] = useState([]);
  const [isEmptyTrashDialogOpen, setIsEmptyTrashDialogOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isFileDetailDialogOpen, setIsFileDetailDialogOpen] = useState(false);
  const [isNewFolderDialogOpen, setIsNewFolderDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleteFolderDialogOpen, setIsDeleteFolderDialogOpen] = useState(false);
  const [isNewCategoryDialogOpen, setIsNewCategoryDialogOpen] = useState(false);
  const [isDeleteCategoryDialogOpen, setIsDeleteCategoryDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderColor, setNewFolderColor] = useState('#1976d2');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('#f44336');
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);

  const [fileDetails, setFileDetails] = useState({
    name: '',
    folderId: '',
    categoryIds: [],
    expiryDate: null,
    reminderDays: 7,
    user: '',
    timestamp: null,
  });

  const availableColors = [
    alpha('#1976d2', 0.6),
    alpha('#2e7d32', 0.6),
    alpha('#ed6c02', 0.6),
    alpha('#9c27b0', 0.6),
    alpha('#f44336', 0.6),
    alpha('#5c6bc0', 0.6)
  ];

  useEffect(() => {
    loadInitialData();
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const res = await axios.get("/api/me", {
        headers: utils.getAuthHeaders(),
        validateStatus: () => true,
      });
      if (res.status === 200) {
        const { success, me } = res.data;
        if (success) {
          if (me.op) {
            setIsAdmin(me.aree.split(',').includes('amministrazione'));
            setIsOp(true);
          } else {
            setIsAdmin(true);
            setDisabilitato(!me.file_manager);
          }
        }
      }
    } catch (_) { }
  }

  const loadInitialData = async () => {
    try {
      const [filesRes, foldersRes, categoriesRes] = await Promise.all([
        axios.get('/api/filemanager/files', { headers: utils.getAuthHeaders() }),
        axios.get('/api/filemanager/folders', { headers: utils.getAuthHeaders() }),
        axios.get('/api/filemanager/categories', { headers: utils.getAuthHeaders() })
      ]);

      setFiles(filesRes.data.files.filter(f => !f.deletedBy));
      setTrash(filesRes.data.files.filter(f => f.deletedBy));
      setFolders(foldersRes.data.folders);
      setCategories(categoriesRes.data.categories);
    } catch (error) {
      console.error("Errore durante il caricamento dei dati:", error);
    }
  };

  const handleFileUpload = async (uploadedFiles) => {
    const formData = new FormData();
    Array.from(uploadedFiles).forEach(file => {
      formData.append('files', file);
    });
    formData.append('data', JSON.stringify({ folderId: selectedFolder?.id }));

    try {
      const res = await axios.post('/api/filemanager/upload', formData, {
        headers: {
          ...utils.getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });

      if (res.data.success) {
        loadInitialData();
      }
    } catch (error) {
      console.error("Errore durante il caricamento del file:", error);
    }
  };

  const saveFileDetails = async () => {
    try {
      const res = await axios.post(`/api/filemanager/updatefile`, { id: selectedFile.id, fileDetails }, {
        headers: utils.getAuthHeaders()
      });

      if (res.data.success) {
        loadInitialData();
        setIsFileDetailDialogOpen(false);
      }
    } catch (error) {
      console.error("Errore durante il salvataggio dei dettagli del file:", error);
    }
  };

  const addNewFolder = async () => {
    if (newFolderName.trim() === '') return;

    try {
      const res = await axios.post('/api/filemanager/folder', {
        name: newFolderName,
        color: newFolderColor
      }, {
        headers: utils.getAuthHeaders()
      });

      if (res.data.success) {
        loadInitialData();
        setNewFolderName('');
        setIsNewFolderDialogOpen(false);
      }
    } catch (error) {
      console.error("Errore durante l'aggiunta della cartella:", error);
    }
  };

  const deleteFile = async (fileId) => {
    try {
      const res = await axios.post(`/api/filemanager/deletefile`, { id: fileId }, {
        headers: utils.getAuthHeaders()
      });
      if (res.data.success) {
        loadInitialData();
        setIsDeleteDialogOpen(false);
      }
    } catch (error) {
      console.error("Errore durante l'eliminazione della cartella:", error);
    }
  };

  const restoreFile = async (fileId) => {
    try {
      const res = await axios.post(`/api/filemanager/restorefile`, { id: fileId }, {
        headers: utils.getAuthHeaders()
      });
      if (res.data.success) {
        loadInitialData();
      }
    } catch (error) {
      console.error("Errore durante l'eliminazione della cartella:", error);
    }
  };

  const emptyTrash = async () => {
    try {
      const res = await axios.post(`/api/filemanager/destroyfiles`, { ids: trash.map(f => f.id) }, {
        headers: utils.getAuthHeaders()
      });
      if (res.data.success) {
        loadInitialData();
      }
    } catch (error) {
      console.error("Errore durante l'eliminazione della cartella:", error);
    }
  };

  const deleteFolder = async (folderId) => {
    try {
      const res = await axios.post(`/api/filemanager/deletefolder`, { id: folderId }, {
        headers: utils.getAuthHeaders()
      });

      if (res.data.success) {
        loadInitialData();
        setIsDeleteFolderDialogOpen(false);
      }
    } catch (error) {
      console.error("Errore durante l'eliminazione della cartella:", error);
    }
  };

  const addNewCategory = async () => {
    if (newCategoryName.trim() === '') return;

    try {
      const res = await axios.post('/api/filemanager/category', {
        name: newCategoryName,
        color: newCategoryColor
      }, {
        headers: utils.getAuthHeaders()
      });

      if (res.data.success) {
        loadInitialData();
        setNewCategoryName('');
        setIsNewCategoryDialogOpen(false);
      }
    } catch (error) {
      console.error("Errore durante l'aggiunta della categoria:", error);
    }
  };

  const deleteCategory = async (categoryId) => {
    try {
      const res = await axios.post(`/api/filemanager/deletecategory`, { id: categoryId }, {
        headers: utils.getAuthHeaders()
      });

      if (res.data.success) {
        loadInitialData();
        setIsDeleteCategoryDialogOpen(false);
      }
    } catch (error) {
      console.error("Errore durante l'eliminazione della categoria:", error);
    }
  };

  const openDeleteDialog = (fileId, event) => {
    event.stopPropagation();
    setFileToDelete(fileId);
    setIsDeleteDialogOpen(true);
  };

  const openDeleteFolderDialog = (folderId) => {
    setFolderToDelete(folderId);
    setIsDeleteFolderDialogOpen(true);
  };

  const openDeleteCategoryDialog = (categoryId, event) => {
    event.stopPropagation();
    setCategoryToDelete(categoryId);
    setIsDeleteCategoryDialogOpen(true);
  };

  const handleCategoryToggle = (categoryId) => {
    setFileDetails(prevDetails => {
      const categoryIds = [...prevDetails.categoryIds];
      const index = categoryIds.indexOf(categoryId);

      if (index > -1) {
        categoryIds.splice(index, 1);
      } else {
        categoryIds.push(categoryId);
      }

      return { ...prevDetails, categoryIds };
    });
  };

  const filteredFiles = selectedFolder === 'trash'
    ? trash
    : files.filter(file => {
      const matchesFolder = selectedFolder ? file.folderId === selectedFolder.id : true;
      const matchesSearch = file.name.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesFolder && matchesSearch;
    });

  const calculateReminderDate = (expiryDate, reminderDays) => {
    if (!expiryDate) return null;

    const date = new Date(expiryDate);
    date.setDate(date.getDate() - reminderDays);
    return date.toLocaleDateString();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const truncateFileName = (fileName, maxLength) => {
    const ellipsis = '...';
    const maxLengthWithoutEllipsis = maxLength - ellipsis.length;

    if (fileName.length <= maxLengthWithoutEllipsis) {
      return fileName;
    }
    return fileName.substring(0, maxLengthWithoutEllipsis) + ellipsis;
  };

  const handleDragStart = (e, file) => {
    e.dataTransfer.setData('text/plain', file.id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, target) => {
    e.preventDefault();
    const fileId = e.dataTransfer.getData('text/plain');
    const file = files.find(file => file.id === fileId);

    if (file) {
      if (target === 'trash') {
        openDeleteDialog(fileId, e);
      } else if (target) {
        const res = await axios.post(`/api/filemanager/updatefile`, { id: fileId, fileDetails: { ...file, folderId: target.id } }, {
          headers: utils.getAuthHeaders()
        });
        if (res.data.success) {
          loadInitialData();
        }
      }
    } else if (target !== 'trash') {
      const droppedFiles = Array.from(e.dataTransfer.files);
      console.log(droppedFiles)
      if (droppedFiles.length > 0) {
        handleFileUpload(droppedFiles);
      }
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box sx={{ width: drawerWidth }}>
      <Box sx={{ p: 2, height: '100%', overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">Cartelle</Typography>
          <IconButton
            color="primary"
            onClick={() => setIsNewFolderDialogOpen(true)}
            size="small"
          >
            <AddIcon />
          </IconButton>
        </Box>

        <List>

          <ListItemButton
            selected={selectedFolder === 'trash'}
            onClick={() => { setSelectedFolder('trash'); setMobileOpen(false); }}
          >
            <ListItemIcon>
              <DeleteIcon />
            </ListItemIcon>
            <ListItemText primary="Cestino" />
          </ListItemButton>

          <ListItemButton
            selected={!selectedFolder}
            onClick={() => { setSelectedFolder(null); setMobileOpen(false); }}
          >
            <ListItemIcon>
              <FolderIcon />
            </ListItemIcon>
            <ListItemText primary="Tutti i file" />
          </ListItemButton>

          {folders.sort((a, b) => a.name.localeCompare(b.name)).map((folder) => (
            <Box key={folder.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <ListItemButton
                selected={selectedFolder?.id === folder.id}
                onClick={() => { setSelectedFolder(folder); setMobileOpen(false); }}
                sx={{ flexGrow: 1 }}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, folder)}
              >
                <ListItemIcon>
                  <FolderIcon sx={{ color: folder.color }} />
                </ListItemIcon>
                <ListItemText primary={folder.name} />
              </ListItemButton>
              {selectedFolder?.id === folder.id && (
                <IconButton
                  edge="end"
                  color="error"
                  onClick={() => openDeleteFolderDialog(folder.id)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          ))}
        </List>

        <Divider />

        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h6">Categorie</Typography>
            <IconButton
              color="primary"
              onClick={() => setIsNewCategoryDialogOpen(true)}
              size="small"
            >
              <AddIcon />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {categories.map((category) => (
              <Chip
                key={category.id}
                icon={<LabelIcon />}
                label={category.name}
                sx={{
                  bgcolor: alpha(category.color, 0.1),
                  color: category.color,
                  borderColor: alpha(category.color, 0.3),
                  '& .MuiChip-icon': { color: category.color }
                }}
                variant="outlined"
                onDelete={(e) => openDeleteCategoryDialog(category.id, e)}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  if (disabilitato) {
    return (
      <ThemeProvider theme={theme}>
        <Paper
          elevation={6}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            px: { xs: 3, sm: 6 },
            py: { xs: 4, sm: 6 },
            maxWidth: 700,
            mx: 'auto',
            my: 6,
            textAlign: 'center',
            borderRadius: 4,
            backgroundColor: 'background.paper',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: 'warning.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="h5" sx={{ color: 'warning.dark', fontWeight: 'bold' }}>
              !
            </Typography>
          </Box>

          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            File Manager disabilitato
          </Typography>

          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 600 }}>
            Il File Manager è uno strumento utile per organizzare i tuoi documenti: puoi caricare file, impostare scadenze, ricevere promemoria, aggiungere note e applicare etichette colorate.
            <br />
            <strong>Contatta lo studio</strong> per abilitare questa funzione.
          </Typography>
        </Paper>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <Hidden smDown>
          <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', height: '100vh' }}>
            <Box sx={{ width: drawerWidth, borderRight: '1px solid #ddd', height: '100%' }}>
              <Box sx={{
                height: '100%',
                overflowY: 'auto',
                overflowX: 'hidden',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0, 0, 0, 0.1)', // Più trasparente
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
                },
              }}>
                {drawer}
              </Box>
            </Box>
            <Box component="main" sx={{ flexGrow: 1, p: 3, overflow: 'auto' }} onDrop={e => handleDrop(e, selectedFolder)} onDragOver={handleDragOver}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5" gutterBottom sx={{ flexGrow: 1 }}>
                  {selectedFolder === 'trash' ? 'Cestino' : selectedFolder ? selectedFolder.name : 'Tutti i file'}
                  <Typography component="span" variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                    ({filteredFiles.length} file)
                  </Typography>
                </Typography>
                <TextField
                  placeholder="Cerca file..."
                  size="small"
                  variant="outlined"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ flexGrow: 1, maxWidth: { xs: '100%', sm: 'auto' } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                {selectedFolder !== 'trash' && (
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    sx={{ flexShrink: 0 }}
                  >
                    Carica File
                    <input
                      type="file"
                      hidden
                      onChange={(e) => handleFileUpload(e.target.files)}
                      multiple
                    />
                  </Button>
                )}
                {selectedFolder === 'trash' && isAdmin && (
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteSweepIcon />}
                    onClick={() => setIsEmptyTrashDialogOpen(true)}
                    sx={{ flexShrink: 0 }}
                  >
                    Svuota Cestino
                  </Button>
                )}
              </Box>

              {filteredFiles.length === 0 ? (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '60vh',
                  color: 'text.secondary',
                  textAlign: 'center'
                }}>
                  {selectedFolder === 'trash' ? (
                    <>
                      <DeleteIcon sx={{ fontSize: 80, opacity: 0.3, mb: 2 }} />
                      <Typography variant="h6">Il cestino è vuoto</Typography>
                      <Typography variant="body2">I file eliminati appariranno qui</Typography>
                    </>
                  ) : (
                    <>
                      <InsertDriveFileIcon sx={{ fontSize: 80, opacity: 0.3, mb: 2 }} />
                      <Typography variant="h6">Nessun file nella cartella selezionata</Typography>
                      <Typography variant="body2">Carica un file o seleziona un'altra cartella</Typography>
                    </>
                  )}
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {filteredFiles.map((file) => {
                    const fileFolder = folders.find(folder => folder.id === file.folderId);
                    const fileCategories = categories.filter(cat => file.categoryIds?.includes(cat.id));
                    const fileExt = file.name.split('.').pop().toLowerCase();

                    return (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={file.id}>
                        <Card
                          draggable
                          onDragStart={(e) => handleDragStart(e, file)}
                          sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            '&:hover': {
                              boxShadow: 6,
                              '& .file-actions': {
                                opacity: 1
                              }
                            }
                          }}
                          onClick={() => {
                            if (selectedFolder !== 'trash') {
                              setSelectedFile(file);
                              setFileDetails({
                                name: file.name,
                                folderId: file.folderId || '',
                                categoryIds: file.categoryIds || [],
                                expiryDate: file.expiryDate ? new Date(file.expiryDate).getTime() : null,
                                reminderDays: file.reminderDays || 7,
                                user: file.user || '',
                                timestamp: file.timestamp || null,
                              });
                              setIsFileDetailDialogOpen(true);
                            }
                          }}
                        >
                          <CardContent sx={{ flexGrow: 1, position: 'relative', pb: 2 }}>
                            <Box className="file-actions" sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              display: 'flex',
                              flexDirection: 'column', // Stack icons vertically
                              gap: 0, // Add some space between the icons
                            }}>
                              {selectedFolder === 'trash' ? (
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    restoreFile(file.id);
                                  }}
                                >
                                  <RestoreIcon fontSize="small" />
                                </IconButton>
                              ) : (
                                <>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={!mimeTypes[fileExt]}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      isOp ? utils.opOpenPdfInNewTab(file.id, mimeTypes[fileExt]) : utils.cliOpenPdfInNewTab(file.id, mimeTypes[fileExt]);
                                    }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      isOp ? utils.opDownload(file.id) : utils.cliDownload(file.id);
                                    }}
                                  >
                                    <GetAppIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openDeleteDialog(file.id, e);
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </>
                              )}
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                              <InsertDriveFileIcon sx={{ mr: 1, color: 'text.secondary' }} />
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="body1" noWrap title={file.name}>
                                  {truncateFileName(file.name, maxFileNameLength)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(file.timestamp).toLocaleDateString()} • {formatFileSize(file.size)}
                                </Typography>
                              </Box>
                            </Box>

                            {fileFolder && (
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <FolderIcon sx={{ fontSize: 16, mr: 0.5, color: fileFolder.color }} />
                                <Typography variant="body2" color="text.secondary">
                                  {fileFolder.name}
                                </Typography>
                              </Box>
                            )}

                            {file.expiryDate && (
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <EventIcon sx={{ fontSize: 16, mr: 0.5, color: 'warning.main' }} />
                                <Typography variant="body2" color="text.secondary">
                                  Scade: {new Date(file.expiryDate).toLocaleDateString()}
                                </Typography>
                              </Box>
                            )}

                            {file.expiryDate && (
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <NotificationsIcon sx={{ fontSize: 16, mr: 0.5, color: '#f5bc42' }} />
                                <Typography variant="body2" color="text.secondary">
                                  Promemoria: {calculateReminderDate(file.expiryDate, file.reminderDays)}
                                </Typography>
                              </Box>
                            )}

                            {selectedFolder === 'trash' && (
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <DeleteIcon sx={{ fontSize: 16, mr: 0.5, color: 'secondary.main' }} />
                                <Typography variant="body2" color="text.secondary">
                                  Eliminato da: {file.deletedBy}
                                </Typography>
                              </Box>
                            )}

                            {fileCategories.length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                {fileCategories.map(cat => (
                                  <Chip
                                    key={cat.id}
                                    label={cat.name}
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.7rem',
                                      bgcolor: alpha(cat.color, 0.1),
                                      color: cat.color,
                                      '& .MuiChip-label': { px: 1 }
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>
          </Box>
        </Hidden>

        <Hidden smUp>
          <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            <Box component="nav">
              <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                  keepMounted: true,
                }}
                sx={{
                  '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                }}
              >
                {drawer}
              </Drawer>
            </Box>

            <Box component="main" sx={{ flexGrow: 1, p: 3, overflow: 'auto' }} onDrop={e => handleDrop(e, selectedFolder)} onDragOver={handleDragOver}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                  sx={{ mr: 2 }}
                >
                  <MenuIcon />
                </IconButton>
                <Typography variant="h5" gutterBottom sx={{ flexGrow: 1 }}>
                  {selectedFolder === 'trash' ? 'Cestino' : selectedFolder ? selectedFolder.name : 'Tutti i file'}
                  <Typography component="span" variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                    ({filteredFiles.length} file)
                  </Typography>
                </Typography>
                <TextField
                  placeholder="Cerca file..."
                  size="small"
                  variant="outlined"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ flexGrow: 1, maxWidth: { xs: '100%', sm: 'auto' } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
                {selectedFolder !== 'trash' && (
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    sx={{ flexShrink: 0 }}
                  >
                    Carica File
                    <input
                      type="file"
                      hidden
                      onChange={(e) => handleFileUpload(e.target.files)}
                      multiple
                    />
                  </Button>
                )}
                {selectedFolder === 'trash' && isAdmin && (
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteSweepIcon />}
                    onClick={() => setIsEmptyTrashDialogOpen(true)}
                    sx={{ flexShrink: 0 }}
                  >
                    Svuota Cestino
                  </Button>
                )}
              </Box>

              {filteredFiles.length === 0 ? (
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '60vh',
                  color: 'text.secondary',
                  textAlign: 'center'
                }}>
                  {selectedFolder === 'trash' ? (
                    <>
                      <DeleteIcon sx={{ fontSize: 80, opacity: 0.3, mb: 2 }} />
                      <Typography variant="h6">Il cestino è vuoto</Typography>
                      <Typography variant="body2">I file eliminati appariranno qui</Typography>
                    </>
                  ) : (
                    <>
                      <InsertDriveFileIcon sx={{ fontSize: 80, opacity: 0.3, mb: 2 }} />
                      <Typography variant="h6">Nessun file nella cartella selezionata</Typography>
                      <Typography variant="body2">Carica un file o seleziona un'altra cartella</Typography>
                    </>
                  )}
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {filteredFiles.map((file) => {
                    const fileFolder = folders.find(folder => folder.id === file.folderId);
                    const fileCategories = categories.filter(cat => file.categoryIds?.includes(cat.id));
                    const fileExt = file.name.split('.').pop().toLowerCase();

                    return (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={file.id}>
                        <Card
                          draggable
                          onDragStart={(e) => handleDragStart(e, file)}
                          sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            '&:hover': {
                              boxShadow: 6,
                              '& .file-actions': {
                                opacity: 1
                              }
                            }
                          }}
                          onClick={() => {
                            if (selectedFolder !== 'trash') {
                              setSelectedFile(file);
                              setFileDetails({
                                name: file.name,
                                folderId: file.folderId || '',
                                categoryIds: file.categoryIds || [],
                                expiryDate: file.expiryDate ? new Date(file.expiryDate).getTime() : null,
                                reminderDays: file.reminderDays || 7,
                                user: file.user || '',
                                timestamp: file.timestamp || null,
                              });
                              setIsFileDetailDialogOpen(true);
                            }
                          }}
                        >
                          <CardContent sx={{ flexGrow: 1, position: 'relative' }}>
                            <Box className="file-actions" sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              display: 'flex',
                              flexDirection: 'column', // Stack icons vertically
                              gap: 0, // Add some space between the icons
                            }}>
                              {selectedFolder === 'trash' ? (
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    restoreFile(file.id);
                                  }}
                                >
                                  <RestoreIcon fontSize="small" />
                                </IconButton>
                              ) : (
                                <>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={!mimeTypes[fileExt]}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      isOp ? utils.opOpenPdfInNewTab(file.id, mimeTypes[fileExt]) : utils.cliOpenPdfInNewTab(file.id, mimeTypes[fileExt]);
                                    }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      isOp ? utils.opDownload(file.id) : utils.cliDownload(file.id);
                                    }}
                                  >
                                    <GetAppIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openDeleteDialog(file.id, e);
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </>
                              )}
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                              <InsertDriveFileIcon sx={{ mr: 1, color: 'text.secondary' }} />
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="body1" noWrap title={file.name}>
                                  {truncateFileName(file.name, maxFileNameLength)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(file.timestamp).toLocaleDateString()} • {formatFileSize(file.size)}
                                </Typography>
                              </Box>
                            </Box>

                            {fileFolder && (
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <FolderIcon sx={{ fontSize: 16, mr: 0.5, color: fileFolder.color }} />
                                <Typography variant="body2" color="text.secondary">
                                  {fileFolder.name}
                                </Typography>
                              </Box>
                            )}

                            {file.expiryDate && (
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <EventIcon sx={{ fontSize: 16, mr: 0.5, color: 'warning.main' }} />
                                <Typography variant="body2" color="text.secondary">
                                  Scade: {new Date(file.expiryDate).toLocaleDateString()}
                                </Typography>
                              </Box>
                            )}

                            {file.expiryDate && (
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <NotificationsIcon sx={{ fontSize: 16, mr: 0.5, color: 'secondary.main' }} />
                                <Typography variant="body2" color="text.secondary">
                                  Promemoria: {calculateReminderDate(file.expiryDate, file.reminderDays)}
                                </Typography>
                              </Box>
                            )}

                            {fileCategories.length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                {fileCategories.map(cat => (
                                  <Chip
                                    key={cat.id}
                                    label={cat.name}
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.7rem',
                                      bgcolor: alpha(cat.color, 0.1),
                                      color: cat.color,
                                      '& .MuiChip-label': { px: 1 }
                                    }}
                                  />
                                ))}
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>
          </Box>
        </Hidden>

        {/* Dialog Dettaglio File */}
        <Dialog
          open={isFileDetailDialogOpen}
          onClose={() => setIsFileDetailDialogOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            Dettagli del file
            <IconButton
              aria-label="close"
              onClick={() => setIsFileDetailDialogOpen(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 3 }}>
              <TextField
                label="Nome del file"
                fullWidth
                disabled
                value={fileDetails.name}
                onChange={(e) => setFileDetails({ ...fileDetails, name: e.target.value })}
                margin="normal"
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Cartella</InputLabel>
                <Select
                  value={fileDetails.folderId}
                  label="Cartella"
                  onChange={(e) => setFileDetails({ ...fileDetails, folderId: e.target.value })}
                >
                  <MenuItem value="">
                    <em>Nessuna cartella</em>
                  </MenuItem>
                  {folders.map((folder) => (
                    <MenuItem key={folder.id} value={folder.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FolderIcon sx={{ mr: 1, color: folder.color }} />
                        {folder.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>Categorie</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {categories.map((category) => (
                  <Chip
                    key={category.id}
                    icon={<LabelIcon />}
                    label={category.name}
                    onClick={() => handleCategoryToggle(category.id)}
                    sx={{
                      bgcolor: fileDetails.categoryIds.includes(category.id)
                        ? alpha(category.color, 0.2)
                        : 'transparent',
                      color: category.color,
                      borderColor: alpha(category.color, 0.5),
                      '& .MuiChip-icon': { color: category.color }
                    }}
                    variant={fileDetails.categoryIds.includes(category.id) ? "filled" : "outlined"}
                  />
                ))}
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Data di scadenza"
                  value={fileDetails.expiryDate ? new Date(fileDetails.expiryDate) : null}
                  onChange={(newDate) => {
                    setFileDetails({
                      ...fileDetails,
                      expiryDate: newDate ? new Date(newDate).getTime() : null
                    });
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: "normal"
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Giorni di anticipo per il promemoria: {fileDetails.reminderDays}
              </Typography>
              <Slider
                value={fileDetails.reminderDays}
                min={1}
                max={30}
                step={1}
                marks={[
                  { value: 1, label: '1' },
                  { value: 7, label: '7' },
                  { value: 14, label: '14' },
                  { value: 30, label: '30' }
                ]}
                valueLabelDisplay="auto"
                onChange={(e, newValue) => setFileDetails({ ...fileDetails, reminderDays: newValue })}
              />
            </Box>

            {fileDetails.expiryDate && (
              <Box sx={{ mb: 2, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <CalendarTodayIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  Data scadenza: {new Date(fileDetails.expiryDate).toLocaleDateString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <AccessTimeIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  Data promemoria: {calculateReminderDate(fileDetails.expiryDate, fileDetails.reminderDays)}
                </Typography>
              </Box>
            )}

            {/* Ultima Modifica e Data */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>Caricamento File</Typography>
              <Typography variant="body2" color="text.secondary">
                Utente: {fileDetails.user || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Data: {fileDetails.timestamp ? new Date(fileDetails.timestamp).toLocaleDateString() : 'N/A'}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsFileDetailDialogOpen(false)}>Annulla</Button>
            <Button variant="contained" onClick={saveFileDetails}>Salva</Button>
          </DialogActions>
        </Dialog>

        {/* Dialog Nuova Cartella */}
        <Dialog
          open={isNewFolderDialogOpen}
          onClose={() => setIsNewFolderDialogOpen(false)}
        >
          <DialogTitle>Nuova cartella</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Nome della cartella"
              fullWidth
              variant="outlined"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
            />

            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              Colore
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {availableColors.map((color) => (
                <Box
                  key={color}
                  component="button"
                  type="button"
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: color,
                    border: newFolderColor === color ? `2px solid ${alpha('#000', 0.5)}` : 'none',
                    cursor: 'pointer',
                    boxShadow: newFolderColor === color ? 3 : 1,
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: 4
                    }
                  }}
                  onClick={() => setNewFolderColor(color)}
                />
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsNewFolderDialogOpen(false)}>Annulla</Button>
            <Button onClick={addNewFolder} variant="contained" disabled={!newFolderName.trim()}>
              Crea cartella
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog Conferma Eliminazione File */}
        <Dialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
        >
          <DialogTitle>Conferma eliminazione</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <WarningIcon color="warning" sx={{ fontSize: 40 }} />
              <Typography>
                Sei sicuro di voler eliminare questo file?
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDeleteDialogOpen(false)}>Annulla</Button>
            <Button onClick={() => deleteFile(fileToDelete)} variant="contained" color="error">
              Elimina
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog Conferma Eliminazione Cartella */}
        <Dialog
          open={isDeleteFolderDialogOpen}
          onClose={() => setIsDeleteFolderDialogOpen(false)}
        >
          <DialogTitle>Conferma eliminazione cartella</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <WarningIcon color="warning" sx={{ fontSize: 40 }} />
              <Typography>
                Sei sicuro di voler eliminare questa cartella e spostare tutti i file al suo interno nel cestino?
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDeleteFolderDialogOpen(false)}>Annulla</Button>
            <Button onClick={() => deleteFolder(folderToDelete)} variant="contained" color="error">
              Elimina
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog Nuova Categoria */}
        <Dialog
          open={isNewCategoryDialogOpen}
          onClose={() => setIsNewCategoryDialogOpen(false)}
        >
          <DialogTitle>Nuova categoria</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Nome della categoria"
              fullWidth
              variant="outlined"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
            />

            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              Colore
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {availableColors.map((color) => (
                <Box
                  key={color}
                  component="button"
                  type="button"
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: color,
                    border: newCategoryColor === color ? `2px solid ${alpha('#000', 0.5)}` : 'none',
                    cursor: 'pointer',
                    boxShadow: newCategoryColor === color ? 3 : 1,
                    transition: 'all 0.2s',
                    '&:hover': {
                      boxShadow: 4
                    }
                  }}
                  onClick={() => setNewCategoryColor(color)}
                />
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsNewCategoryDialogOpen(false)}>Annulla</Button>
            <Button onClick={addNewCategory} variant="contained" disabled={!newCategoryName.trim()}>
              Crea categoria
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog Conferma Eliminazione Categoria */}
        <Dialog
          open={isDeleteCategoryDialogOpen}
          onClose={() => setIsDeleteCategoryDialogOpen(false)}
        >
          <DialogTitle>Conferma eliminazione categoria</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <WarningIcon color="warning" sx={{ fontSize: 40 }} />
              <Typography>
                Sei sicuro di voler eliminare questa categoria? Questa azione non può essere annullata.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsDeleteCategoryDialogOpen(false)}>Annulla</Button>
            <Button onClick={() => deleteCategory(categoryToDelete)} variant="contained" color="error">
              Elimina
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog Conferma Svuota Cestino */}
        <Dialog
          open={isEmptyTrashDialogOpen}
          onClose={() => setIsEmptyTrashDialogOpen(false)}
        >
          <DialogTitle>Conferma svuotamento cestino</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <WarningIcon color="warning" sx={{ fontSize: 40 }} />
              <Typography>
                Sei sicuro di voler svuotare il cestino? Questa azione distrugge tutti i file nel cestino e non può essere annullata.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsEmptyTrashDialogOpen(false)}>Annulla</Button>
            <Button onClick={() => {
              emptyTrash();
              setIsEmptyTrashDialogOpen(false);
            }} variant="contained" color="error">
              Svuota
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}
